import React from "react";
import { Button, Tooltip } from "@heroui/react";
import { ArrowLeftIcon, ArrowRightIcon, InformationCircleIcon } from "@heroicons/react/24/outline";
import { TFunction } from "react-i18next";
import { State, ValidationErrors } from "../../types/types";
import hoowlrLogo from "../../assets/images/logo.svg";
import loginBanner from "../../assets/images/login-banner.png";

const tertiaryButtonClass =
  "h-10 rounded-2xl lg:rounded-3xl bg-[#EBEBEC] px-4 text-xs sm:text-sm lg:text-base font-semibold lg:font-medium leading-5 lg:leading-6 text-[#18181B] hover:bg-[#dcdcde] active:scale-[0.98] transition-all";

interface ProfileImageUploadStageProps {
  t: TFunction<"signUpSetupStages", undefined>;
  state: State;
  validationErrors: ValidationErrors;
  handleFileChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleNext: (e: React.FormEvent | any, nextStage: string | any) => void;
  handleFormSubmit: (e: React.FormEvent | any) => void;
  handleBack: () => void;
  isSubmitting: boolean;
}

const ProfileImageUploadStage: React.FC<ProfileImageUploadStageProps> = ({
  t,
  state,
  validationErrors,
  handleFileChange,
  handleNext,
  handleFormSubmit,
  handleBack,
  isSubmitting,
}) => {
  const isOrganization = state.formData.profileType === "ORGANIZATION";

  return (
    <div className="flex min-h-dvh w-full bg-white font-['Plus_Jakarta_Sans',sans-serif] text-[#18181B]">
      {/* LEFT COLUMN */}
      <div className="flex min-w-0 flex-1 flex-col">
        <div
          className="
            flex min-h-dvh flex-col justify-between
            px-6 py-6 sm:px-12 sm:py-8
            lg:py-9
            [@media(min-height:701px)_and_(max-height:800px)]:lg:py-6
            [@media(max-height:700px)]:lg:py-3
          "
        >
          {/* Header: logo */}
          <div className="flex w-full items-center justify-between">
            <img
              src={hoowlrLogo}
              alt="Hoowlr"
              className="
                h-9 w-auto object-contain object-left
                lg:h-16 lg:w-48
                [@media(min-height:701px)_and_(max-height:800px)]:lg:h-14
                [@media(max-height:700px)]:lg:h-10
              "
            />
          </div>

          {/* Centered content */}
          <div className="flex flex-1 flex-col items-center justify-center my-auto w-full py-2">
            <div className="flex w-full flex-col items-center gap-1 sm:gap-2 text-center mb-4 sm:mb-6 lg:mb-8 [@media(max-height:700px)]:mb-2">
              <h1 className="text-xl sm:text-2xl lg:text-4xl font-bold lg:font-extrabold leading-tight lg:leading-10 text-[#18181B]">
                <span className="inline-flex items-center gap-2">
                  {t("profileImageUpload.title")}
                  <Tooltip
                    className="w-[300px]"
                    placement="bottom"
                    content={
                      <div className="text-xs p-1">
                        <span className="font-semibold block mb-0.5">
                          {t("mainProfileImageTitle")}
                        </span>
                        <span>{t("mainProfileImageDescription")}</span>
                      </div>
                    }
                  >
                    <InformationCircleIcon className="w-4 h-4 text-gray-400 cursor-pointer" />
                  </Tooltip>
                </span>
              </h1>
              <p className="text-xs sm:text-sm lg:text-base leading-4 lg:leading-6 text-[#71717A]">
                {t("profileImageUpload.subtitle")}
              </p>
            </div>

            <form
              noValidate
              onSubmit={(e) => {
                if (isOrganization) {
                  handleFormSubmit(e);
                  return;
                }
                handleNext(
                  e,
                  state.formData.profileType === "FIGHTER"
                    ? "fighterFormBasic"
                    : "managerForm"
                );
              }}
              className="
                flex w-full max-w-96 flex-col gap-4 sm:gap-5 lg:gap-6
                [@media(min-height:701px)_and_(max-height:800px)]:gap-4 lg:[@media(min-height:701px)_and_(max-height:800px)]:gap-5
                [@media(max-height:700px)]:gap-3 lg:[@media(max-height:700px)]:gap-4
              "
            >
              <div
                className="
                  flex flex-col gap-3 lg:gap-4
                  [@media(max-height:700px)]:gap-2.5 lg:[@media(max-height:700px)]:gap-3
                "
              >
                {(state.formData.profileImageUrl || state.formData.profileImageFile) && (
                  <div className="text-center">
                    <img
                      src={
                        state.formData.profileImageFile
                          ? URL.createObjectURL(state.formData.profileImageFile)
                          : state.formData.profileImageUrl!
                      }
                      alt="Profile Preview"
                      className="w-24 h-24 lg:w-28 lg:h-28 object-cover rounded-full mx-auto border-2 border-slate-200 shadow-sm"
                      referrerPolicy="no-referrer"
                      onError={(e) => {
                        e.currentTarget.src = "/default-avatar.png";
                      }}
                    />
                    <a
                      href={
                        state.formData.profileImageFile
                          ? URL.createObjectURL(state.formData.profileImageFile)
                          : state.formData.profileImageUrl
                      }
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#0485F7] hover:underline text-xs mt-2 inline-block font-medium"
                    >
                      {t("profileImageUpload.viewFullImage")}
                    </a>
                  </div>
                )}

                <div
                  aria-invalid={!!validationErrors["profileImageFile"]}
                  tabIndex={validationErrors["profileImageFile"] ? -1 : undefined}
                  className="border-2 border-dashed border-slate-200 lg:border-[#EBEBEC] bg-slate-50 lg:bg-[#EBEBEC]/40 hover:bg-slate-100 h-32 flex items-center justify-center rounded-2xl transition-all"
                >
                  <label
                    htmlFor="profileImageFile"
                    className="cursor-pointer flex flex-col items-center p-4 text-center"
                  >
                    <svg
                      className="w-8 h-8 text-slate-400 mb-1.5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.8}
                        d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z"
                      />
                    </svg>
                    <span className="text-xs sm:text-sm font-semibold text-[#18181B]">
                      {t("profileImageUpload.uploadLabel")}
                    </span>
                    <span className="text-[11px] sm:text-xs text-[#71717A] mt-0.5">
                      {t("profileImageUpload.uploadDescription")}
                    </span>
                    <input
                      id="profileImageFile"
                      name="profileImageFile"
                      type="file"
                      accept="image/png,image/jpeg,image/jpg"
                      onChange={handleFileChange}
                      className="hidden"
                    />
                  </label>
                </div>

                {validationErrors["profileImageFile"] && (
                  <p className="text-red-500 text-xs text-left ml-1" role="alert">
                    {validationErrors["profileImageFile"]}
                  </p>
                )}
              </div>

              {/* Footer: Back / Next */}
              <div className="flex w-full items-center justify-between gap-3 pt-4 sm:pt-6 [@media(max-height:700px)]:pt-3">
                <Button
                  onPress={handleBack}
                  variant="flat"
                  startContent={<ArrowLeftIcon className="h-4 w-4" />}
                  className={tertiaryButtonClass}
                >
                  {t("navigation.back")}
                </Button>

                <Button
                  type="submit"
                  isLoading={isSubmitting}
                  isDisabled={isSubmitting}
                  endContent={!isSubmitting && <ArrowRightIcon className="h-4 w-4" />}
                  className="h-10 rounded-2xl lg:rounded-3xl bg-[#0485F7] px-4 text-xs sm:text-sm lg:text-base font-semibold lg:font-medium leading-5 lg:leading-6 text-white hover:bg-[#0374d6] active:scale-[0.99] transition-all shadow-sm disabled:cursor-not-allowed disabled:bg-gray-400"
                >
                  {isSubmitting
                    ? t("navigation.submitting")
                    : isOrganization
                    ? t("navigation.submit")
                    : t("navigation.next")}
                </Button>
              </div>
            </form>
          </div>

          {/* Footer */}
          <p className="pt-2 pb-1 text-center text-[11px] sm:text-xs font-normal leading-4 text-[#71717A]">
            {t("footer_copyright", "© Hoowlr 2026, All rights reserved")}
          </p>
        </div>
      </div>

      {/* RIGHT BANNER (hidden below lg) */}
      <div className="hidden shrink-0 p-9 lg:sticky lg:top-0 lg:flex lg:h-dvh lg:w-[45%] xl:w-[55%] 2xl:w-[62%]">
        <div className="flex h-full w-full items-center justify-center overflow-hidden rounded-[2rem] bg-[#1F6BFF]">
          <img
            src={loginBanner}
            alt=""
            className="max-h-full max-w-full object-contain"
          />
        </div>
      </div>
    </div>
  );
};

export default ProfileImageUploadStage;
